/**
 * Guides pédagogiques MPDS — Lot 2 : Toxique et environnemental
 */
export const buildTeachingGuidesLot2 = ({ Heart, AlertTriangle, ShieldAlert, Clock, Users }) => ({

  overdose_empoisonnement: {
    title: 'Overdose / Empoisonnement',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `L\'overdose d\'opiacés est la cause #1 de décès évitable chez les 25-44 ans au Canada. La naloxone (Narcan) intranasale ou intramusculaire renverse l\'effet en 2-5 minutes. Le rôle du répartiteur : reconnaître l\'overdose, guider la naloxone si disponible, initier la RCR si arrêt respiratoire.`,
    stats: [
      { icon: Heart, text: 'Plus de 7 500 décès par opiacés au Canada en 2023.' },
      { icon: Clock, text: 'Le fentanyl tue en 2-5 minutes sans intervention.' },
      { icon: AlertTriangle, text: 'La naloxone est disponible sans ordonnance dans toutes les pharmacies du Québec.' },
    ],
    objectives: [
      'Reconnaître les signes d\'overdose opiacés (myosis, dépression respiratoire, inconscience).',
      'Différencier overdose stimulants (agitation) vs dépresseurs (somnolence).',
      'Guider l\'administration de naloxone à distance.',
      'Initier la RCR si arrêt respiratoire complet.',
      'Sécuriser le contenu ingéré pour le Centre antipoison.',
    ],
    phases: [
      { n: 2, title: 'État de conscience et respiration',
        why: 'L\'overdose tue par dépression respiratoire.',
        how: ['« Est-il conscient ? Réagit-il à la voix, au pincement ? »', '« Respire-t-il lentement, irrégulièrement, pas du tout ? »', '« Quelle couleur ont ses lèvres et son visage ? »'],
        pitfalls: ['Confondre sommeil profond et overdose', 'Ne pas reconnaître respiration agonale'] },
      { n: 3, title: 'Substance et quantité',
        why: 'Détermine antidote et urgence.',
        how: ['« Que pensez-vous qu\'il a pris ? »', '« Combien ? À quelle heure ? »', '« Avez-vous la boîte, la seringue, les pilules ? »', '« A-t-il mélangé avec de l\'alcool ? »'],
        pitfalls: ['Faire perdre du temps si overdose évidente'] },
      { n: 4, title: 'Naloxone disponible',
        why: 'Renverse l\'overdose opiacés en quelques minutes.',
        how: ['« Avez-vous de la naloxone (Narcan) ? »', '« Spray nasal ou seringue ? »', '« Si oui → ADMINISTREZ, je vous guide »'],
        pitfalls: ['Hésiter à ordonner la naloxone'] },
    ],
    citizen_behavior: {
      title: 'Guider la naloxone et la RCR par téléphone',
      tone: ['Voix calme et directive', 'Ne pas juger la consommation', 'Rassurer sur l\'amnistie (loi de bons samaritains)'],
      phrases_utiles: ['« Vous ne serez pas poursuivi pour appeler le 911 — la loi vous protège. »', '« Renversez-le sur le dos. Inclinez la tête vers l\'arrière. »', '« Insérez le spray dans une narine et appuyez fermement. »', '« La naloxone agit en 2-5 minutes. »'],
      phrases_a_eviter: ['« Pourquoi a-t-il pris ça ? »', '« Faites-le marcher »', '« Donnez-lui du café »', '« Faites-le vomir »'],
      panique_gestion: ['Si témoin a aussi consommé → focaliser sur l\'aide à autrui', 'Si pas de naloxone disponible → RCR + ventilation', 'Si réveil agressif après naloxone → reculer pour sécurité'],
    },
    red_flags: ['Respiration < 10/min', 'Cyanose des lèvres', 'Pupilles en pointe d\'épingle (opiacés)', 'Inconscience profonde', 'Seringues visibles', 'Polyconsommation suspectée', 'Effort respiratoire avec bruits anormaux'],
    pre_arrival: [
      'Position latérale de sécurité si respire',
      'Si arrêt respiratoire → RCR avec ventilations privilégiées',
      'Naloxone intranasale : 1 dose par narine, peut répéter après 2-3 min',
      'Naloxone IM : cuisse externe, peut répéter',
      'NE PAS donner à boire, manger, douche froide',
      'NE PAS laisser seul après réveil',
      'Conserver substances pour identification',
    ],
    common_errors: [
      'Hésiter à donner naloxone',
      'Faire marcher la personne pour la "réveiller"',
      'Mettre sous la douche froide',
      'Croire que la naloxone est dangereuse si pas d\'overdose (elle est sécuritaire)',
    ],
    discussion_questions: [
      'Jeune homme inconscient dans toilettes, seringue à côté. Conduite ?',
      'Femme âgée ayant pris 30 comprimés d\'acétaminophène. Antidote spécifique ?',
      'Patient qui se réveille agressif après naloxone. Pourquoi ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 23 — Overdose/Poisoning',
      'Santé Canada — Trousses de naloxone gratuites',
      'Centre antipoison du Québec : 1-800-463-5060',
    ],
  },

  diabete: {
    title: 'Diabète / Hypoglycémie',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `L\'hypoglycémie (< 4 mmol/L) peut tuer en moins d\'une heure si non corrigée. Elle se présente comme une multitude de symptômes neurologiques (confusion, agressivité, convulsions, coma) facilement confondus avec un AVC, une intoxication ou une maladie psychiatrique. Toujours penser "diabète" face à une altération de l\'état mental.`,
    stats: [
      { icon: Heart, text: '~3 millions de Canadiens vivent avec le diabète.' },
      { icon: Clock, text: 'Une hypoglycémie sévère cause des lésions cérébrales en 1-6 h.' },
      { icon: AlertTriangle, text: '40 % des appels pour "AVC" chez le diabétique sont des hypoglycémies.' },
    ],
    objectives: [
      'Suspecter une hypo devant tout trouble neurologique chez un diabétique.',
      'Reconnaître les signes classiques (sueurs, tremblements, faim, confusion).',
      'Guider la prise de sucre rapide chez le patient conscient.',
      'NE PAS donner de sucre à un patient inconscient (risque fausse route).',
      'Différencier hypo vs hyperglycémie (ACD).',
    ],
    phases: [
      { n: 2, title: 'Antécédents diabète',
        why: 'Oriente immédiatement vers hypo si trouble mental.',
        how: ['« Est-il diabétique ? »', '« Prend-il de l\'insuline ou des comprimés ? »', '« A-t-il mangé aujourd\'hui ? Quand ? »', '« A-t-il fait du sport intense récemment ? »'],
        pitfalls: ['Ne pas penser hypo si patient non diabétique connu', 'Oublier que les comprimés (sulfonylurées) peuvent causer hypos'] },
      { n: 3, title: 'Symptômes actuels',
        why: 'Différencie hypo (rapide, sueurs) vs hyper (lente, déshydratation).',
        how: ['« Est-il en sueurs, tremblant, pâle ? »', '« Est-il confus, agressif, comme ivre ? »', '« A-t-il bu beaucoup, uriné beaucoup ces derniers jours ? »', '« A-t-il une haleine fruitée (acétone) ? »'],
        pitfalls: ['Confondre agressivité hypoglycémique et intoxication alcoolique'] },
      { n: 4, title: 'Capacité d\'avaler',
        why: 'Détermine si on peut donner du sucre par la bouche.',
        how: ['« Est-il assez conscient pour avaler en sécurité ? »', '« Peut-il s\'asseoir ? »'],
        pitfalls: ['Donner du sucre à un patient inconscient'] },
    ],
    citizen_behavior: {
      title: 'Guider la prise de sucre',
      tone: ['Voix rassurante — l\'hypo est réversible en minutes', 'Donner instructions précises sur quantité', 'Recompter avec le témoin'],
      phrases_utiles: ['« Donnez-lui 15 g de sucre rapide : jus d\'orange, miel, comprimés de glucose. »', '« Attendez 15 min, vérifiez si ça va mieux. »', '« S\'il ne va pas mieux, redonnez 15 g. »', '« Si conscient et améliore, qu\'il mange une collation (pain, fromage). »'],
      phrases_a_eviter: ['« Donnez-lui du chocolat » (graisse ralentit l\'absorption)', '« Donnez du jus de fruit "diète" » (pas de sucre)', '« Couchez-le sur le dos avec du sucre » (risque fausse route)'],
      panique_gestion: ['Si patient agressif → c\'est l\'hypo qui parle, ne pas confronter', 'Si refuse de manger → ne pas forcer si inconscient', 'Demander glucomètre disponible'],
    },
    red_flags: ['Convulsions', 'Coma', 'Incapacité d\'avaler', 'Confusion sévère', 'Pâleur extrême', 'Glycémie < 3,0 mmol/L au glucomètre', 'Sueurs profuses', 'Hypoglycémies récidivantes', 'Insuline et pas de repas pris'],
    pre_arrival: [
      'Si conscient → 15 g de sucre rapide PO',
      'Recontrôler après 15 min',
      'Si inconscient → ne RIEN donner par la bouche',
      'Si inconscient et glucagon disponible → IM cuisse',
      'Position latérale de sécurité si inconscient',
      'Surveiller la respiration',
      'Faire collation glucides lents après amélioration',
    ],
    common_errors: [
      'Donner du sucre à un patient inconscient',
      'Faire du chocolat ou des sucreries grasses',
      'Sous-doser (1 carré de sucre = ~3 g, il en faut 15)',
      'Ne pas penser à l\'hypo si patient non identifié comme diabétique',
    ],
    discussion_questions: [
      'Patient diabétique trouvé confus dans sa voiture. AVC ou hypo ?',
      'Femme âgée diabétique sous insuline, n\'a pas mangé, convulsive. Action ?',
      'Patient diabétique avec respiration rapide profonde, haleine fruitée. Hypo ou ACD ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 13 — Diabetic Problems',
      'Diabète Canada — Guidelines 2023',
      'CDA — Hypoglycemia management',
    ],
  },

  monoxyde_carbone: {
    title: 'Intoxication au Monoxyde de Carbone',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `Le CO est un gaz invisible, inodore et mortel. Les intoxications surviennent souvent en hiver (chauffage défectueux, génératrices, voitures dans le garage). Le risque #1 du répartiteur : ne PAS envoyer le témoin secourir la victime à l\'intérieur — il deviendrait à son tour victime.`,
    stats: [
      { icon: Heart, text: 'Le CO cause ~50 décès et 1000 hospitalisations/an au Canada.' },
      { icon: Clock, text: 'À forte concentration, le CO tue en quelques minutes.' },
      { icon: AlertTriangle, text: 'Les symptômes (céphalées, nausées) sont souvent confondus avec une grippe.' },
    ],
    objectives: [
      'Reconnaître un tableau d\'intoxication CO (céphalées + plusieurs personnes atteintes).',
      'Prioriser la sécurité du témoin AVANT le sauvetage.',
      'Coordonner l\'évacuation et l\'aération du bâtiment.',
      'Alerter les pompiers pour ventilation et détection.',
      'Identifier la source pour éviter la récidive.',
    ],
    phases: [
      { n: 2, title: 'Symptômes et tableau collectif',
        why: 'Plusieurs personnes malades au même endroit = CO probable.',
        how: ['« Plusieurs personnes au même endroit sont-elles malades ou somnolentes ? »', '« Maux de tête, nausées, vertiges, faiblesse ? »', '« Les animaux domestiques semblent-ils malades ou morts ? »'],
        pitfalls: ['Confondre avec grippe ou gastro-entérite'] },
      { n: 3, title: 'Source possible',
        why: 'Identifie le danger pour les pompiers.',
        how: ['« Chauffage à combustion (gaz, mazout, bois) ? »', '« Voiture dans le garage avec moteur allumé ? »', '« Génératrice utilisée à l\'intérieur ? »', '« Foyer ou poêle à bois ? »'],
        pitfalls: ['Ne pas demander si génératrice (très fréquent lors de pannes électriques)'] },
      { n: 4, title: 'Localisation et accessibilité',
        why: 'Détermine la conduite de sauvetage SÉCURISÉE.',
        how: ['« Êtes-vous à l\'extérieur ou à l\'intérieur ? »', '« Pouvez-vous ouvrir portes et fenêtres SANS entrer ? »', '« Quelqu\'un est-il encore à l\'intérieur ? Conscient ? »'],
        pitfalls: ['Faire entrer le témoin (DANGER MORTEL)'] },
    ],
    citizen_behavior: {
      title: 'Sécuriser le témoin avant tout',
      tone: ['Voix très ferme — la priorité est de ne pas avoir 2 victimes', 'Donner consignes claires et hiérarchisées', 'Ne pas autoriser l\'entrée'],
      phrases_utiles: ['« N\'entrez PAS dans le bâtiment, restez dehors. »', '« Ouvrez portes et fenêtres SI vous pouvez sans entrer. »', '« Les pompiers vont entrer avec leur équipement spécialisé. »', '« Comptez les personnes manquantes pour les pompiers. »'],
      phrases_a_eviter: ['« Allez chercher la victime » (DANGER MORTEL)', '« Ouvrez la porte d\'entrée et restez à l\'intérieur »'],
      panique_gestion: ['Si témoin déjà à l\'intérieur → sortir IMMÉDIATEMENT', 'Si témoin veut secourir un proche → expliquer le risque mortel pour les deux', 'Si symptômes apparaissent chez le témoin → s\'asseoir à l\'extérieur'],
    },
    red_flags: ['Plusieurs personnes symptomatiques', 'Animaux malades ou morts', 'Inconscience', 'Convulsions', 'Génératrice ou véhicule en marche à l\'intérieur', 'Coloration rose-cerise (rare mais pathognomonique)', 'Confusion progressive'],
    pre_arrival: [
      'SORTIR à l\'air libre immédiatement',
      'NE PAS retourner à l\'intérieur',
      'Ouvrir portes et fenêtres SI possible sans entrer',
      'Couper la source si accessible depuis l\'extérieur (génératrice, etc.)',
      'Compter les personnes pour pompiers',
      'Si conscient mais symptomatique → s\'asseoir, respiration calme',
      'Surveiller détérioration des symptômes',
      'Pompiers OBLIGATOIRES pour vérifier la sécurité avant retour',
    ],
    common_errors: [
      'Permettre au témoin de retourner à l\'intérieur',
      'Sous-estimer (« c\'est juste un mal de tête »)',
      'Ne pas envoyer les pompiers',
      'Oublier la génératrice comme source',
    ],
    discussion_questions: [
      'Famille de 4 personnes toutes nauséeuses depuis l\'allumage du chauffage. Action ?',
      'Homme retrouvé inconscient dans son garage, voiture en marche. Conduite ?',
      'Mère qui veut entrer chercher ses enfants inconscients. Que lui dites-vous ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 8 — Carbon Monoxide/Inhalation',
      'Santé Canada — Avis sur le monoxyde de carbone',
      'CAPQ — Centre antipoison du Québec',
    ],
  },

  pendaison: {
    title: 'Pendaison / Strangulation',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `La pendaison est un acte souvent suicidaire qui peut être réversible si la victime est dépendue rapidement et que l\'asphyxie n\'a pas dépassé 4-6 minutes. Le répartiteur doit gérer simultanément l\'aspect médical (RCR si besoin), traumatique (suspicion lésion cervicale) et émotionnel (témoin choqué).`,
    stats: [
      { icon: Heart, text: 'Au Québec, ~1000 suicides/an — pendaison = méthode la plus fréquente.' },
      { icon: Clock, text: 'La survie est possible jusqu\'à ~10 min de suspension complète.' },
      { icon: AlertTriangle, text: '50 % des pendaisons "réussies" se font sans suspension complète (semi-suspension).' },
    ],
    objectives: [
      'Décider du dépendage immédiat (témoin) vs attente des secours.',
      'Suspecter une lésion cervicale et manipuler avec précaution.',
      'Initier la RCR si arrêt cardio-respiratoire.',
      'Soutenir émotionnellement le témoin (souvent un proche).',
      'Coordonner avec les forces policières (scène à préserver si décès).',
    ],
    phases: [
      { n: 2, title: 'Encore suspendu ?',
        why: 'Si oui, le dépendage immédiat sauve la vie.',
        how: ['« Est-il encore suspendu ou a-t-il été dépendu ? »', '« Si suspendu → pouvez-vous le dépendre SAFE ? Y a-t-il un risque pour vous ? »', '« Soutenez son corps PENDANT que vous coupez le lien, ne le laissez pas tomber. »'],
        pitfalls: ['Faire tomber la victime sur le sol — aggrave lésions cervicales'] },
      { n: 3, title: 'État après dépendage',
        why: 'Détermine RCR vs surveillance.',
        how: ['« Respire-t-il ? Sa coloration ? »', '« Est-il conscient ? Réagit-il ? »', '« Allongez-le doucement sur le dos, en maintenant la tête alignée. »'],
        pitfalls: ['Mobiliser le cou sans précaution'] },
      { n: 4, title: 'Durée et contexte',
        why: 'Estime les chances de récupération.',
        how: ['« Depuis combien de temps environ ? »', '« A-t-il pris des médicaments, de l\'alcool ? »', '« A-t-il laissé une lettre, des indices ? »'],
        pitfalls: ['Investiguer le motif avant de stabiliser'] },
    ],
    citizen_behavior: {
      title: 'Soutenir un proche après pendaison',
      tone: ['Voix très douce et présente', 'Pas de jugement, pas de "pourquoi"', 'Concentrer sur l\'action immédiate plutôt que les questions'],
      phrases_utiles: ['« Je suis avec vous, on va l\'aider. »', '« Faites exactement ce que je dis, je vous guide. »', '« Vous faites tout ce qu\'il faut. »'],
      phrases_a_eviter: ['« Pourquoi a-t-il fait ça ? »', '« Vous auriez dû voir les signes »', '« Calmez-vous »'],
      panique_gestion: ['Si témoin paralysé → instructions très simples, une à la fois', 'Si plusieurs témoins → désigner UN seul auteur des gestes', 'Préparer aux ambulanciers + police'],
    },
    red_flags: ['Suspension > 5 minutes', 'Absence de respiration après dépendage', 'Inconscience profonde', 'Cyanose marquée du visage', 'Marques de strangulation', 'Pétéchies aux yeux', 'Mousse aux lèvres', 'Contexte suicidaire avéré'],
    pre_arrival: [
      'Dépendre la victime EN SOUTENANT son corps (ne pas la laisser tomber)',
      'Allonger doucement sur le dos, tête alignée (suspicion trauma cervical)',
      'Évaluer respiration et conscience',
      'Si arrêt → RCR (privilégier ventilations, l\'asphyxie a précédé l\'arrêt cardiaque)',
      'Couvrir d\'une couverture',
      'NE PAS retirer la corde/lien — laisser pour police',
      'Si conscient → maintenir tête alignée, calme',
      'Pompiers + ambulance + police OBLIGATOIRES',
    ],
    common_errors: [
      'Laisser tomber la victime en coupant',
      'Mobiliser le cou sans précaution',
      'Oublier que c\'est aussi une scène policière',
      'Sous-estimer la possibilité de survie',
    ],
    discussion_questions: [
      'Adolescent retrouvé pendu par sa sœur. Comment communiquer ?',
      'Patient dépendu inconscient mais respire. Position et surveillance ?',
      'Pendaison incomplète (semi-suspension) — pourquoi est-ce mortel quand même ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 24 — Strangulation/Hanging',
      'Suicide Prevention Canada — Lignes directrices',
      'Coroner du Québec — Statistiques annuelles',
    ],
  },

  electrisation: {
    title: 'Électrisation / Électrocution',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `Le courant électrique peut causer : arrêt cardiaque (fibrillation ventriculaire), arrêt respiratoire, brûlures profondes invisibles, lésions musculaires sévères. Le premier réflexe du répartiteur : sécuriser la source AVANT toute approche — le témoin pourrait devenir la 2e victime.`,
    stats: [
      { icon: Heart, text: '~150 électrocutions mortelles/an au Canada.' },
      { icon: AlertTriangle, text: 'Le courant alternatif (maison) cause souvent fibrillation ventriculaire.' },
      { icon: Clock, text: 'Le courant continu (industriel) cause souvent projection à distance.' },
    ],
    objectives: [
      'Sécuriser la source d\'électricité AVANT toute approche.',
      'Reconnaître les arrêts cardiaques post-électrisation.',
      'Évaluer les brûlures d\'entrée et de sortie.',
      'Suspecter les lésions traumatiques associées (chute, projection).',
      'Coordonner avec Hydro-Québec pour ligne haute tension.',
    ],
    phases: [
      { n: 2, title: 'Source toujours active ?',
        why: 'Aucune approche sans coupure du courant.',
        how: ['« Le courant est-il toujours actif ? »', '« Pouvez-vous couper le disjoncteur principal ? »', '« Est-ce une ligne aérienne ou un appareil domestique ? »', '« Si ligne extérieure → reculez à 10 m minimum »'],
        pitfalls: ['Toucher victime avant coupure', 'Sous-estimer une ligne haute tension'] },
      { n: 3, title: 'Type de courant',
        why: 'Domestique (120/240V) vs industriel (haute tension) — gravité très différente.',
        how: ['« Courant domestique (prise, appareil) ou ligne haute tension ? »', '« Combien de temps environ ? »', '« A-t-il été projeté loin ? »'],
        pitfalls: ['Confondre arrêt cardiaque post-choc et inconscience post-traumatique'] },
      { n: 4, title: 'État du patient',
        why: 'Évalue urgence cardiaque + lésions traumatiques.',
        how: ['« Est-il conscient ? »', '« Respire-t-il normalement ? »', '« Voyez-vous des brûlures noires ou des marques d\'entrée/sortie ? »', '« A-t-il été projeté, est-il tombé d\'une hauteur ? »'],
        pitfalls: ['Oublier de chercher point d\'entrée ET de sortie'] },
    ],
    citizen_behavior: {
      title: 'Gérer la peur électrique',
      tone: ['Voix très ferme sur la sécurité', 'Insister sur la distance pour ligne haute tension', 'Pas de panique mais directives strictes'],
      phrases_utiles: ['« Ne le touchez PAS tant que le courant n\'est pas coupé. »', '« Allez au disjoncteur principal, descendez l\'interrupteur principal. »', '« Pour une ligne extérieure, restez à 10 mètres et n\'approchez jamais. »'],
      phrases_a_eviter: ['« Tirez-le avec une perche en bois » (faux sentiment de sécurité)', '« Touchez-le pour voir s\'il respire » avant coupure'],
      panique_gestion: ['Si témoin veut absolument intervenir → expliquer qu\'il deviendrait victime #2', 'Si plusieurs victimes → numéroter dans l\'ordre d\'évacuation', 'Préparer l\'arrivée Hydro-Québec'],
    },
    red_flags: ['Arrêt cardiaque', 'Arrêt respiratoire', 'Inconscience prolongée', 'Brûlures multiples', 'Chute associée (suspicion trauma)', 'Convulsions post-choc', 'Trouble du rythme cardiaque perçu', 'Haute tension impliquée', 'Source non sécurisée'],
    pre_arrival: [
      'COUPER LE COURANT AVANT TOUT CONTACT',
      'Pour ligne haute tension → rester à 10 m, attendre Hydro-Québec',
      'Si arrêt cardiaque → RCR + DEA',
      'Mobiliser avec précautions cervicales (chute possible)',
      'Refroidir les brûlures à l\'eau froide 20 min',
      'Couvrir les brûlures avec linge propre',
      'Surveiller arythmie même chez patient conscient (peut survenir tardivement)',
      'NE PAS donner à boire',
    ],
    common_errors: [
      'Toucher la victime avant coupure',
      'Approcher d\'une ligne haute tension',
      'Sous-estimer les brûlures invisibles (en profondeur)',
      'Renvoyer le patient à la maison après "petit choc"',
    ],
    discussion_questions: [
      'Travailleur ayant touché une ligne 25 000 V. Pourquoi attendre Hydro-Québec ?',
      'Enfant ayant mordu un câble électrique. Risques particuliers ?',
      'Patient conscient après électrisation domestique, semble bien. Faut-il transporter ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 15 — Electrocution/Lightning',
      'Hydro-Québec — Procédures d\'urgence',
      'CSA Z462 — Sécurité électrique au travail',
    ],
  },

  personne_malade: {
    title: 'Personne Malade (symptômes vagues)',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `« Je ne me sens pas bien » est l\'un des appels les plus difficiles à trier. Derrière ce flou peut se cacher : sepsis débutant, syndrome coronarien atypique, AVC postérieur, déshydratation sévère. Le répartiteur doit poser les bonnes questions structurées pour révéler le signal sous le bruit.`,
    stats: [
      { icon: Heart, text: 'Près de 30 % des appels MPDS sont initialement classés "sick person".' },
      { icon: AlertTriangle, text: 'Le sepsis tue plus que les cancers du sein, prostate et VIH réunis.' },
      { icon: Clock, text: 'Chaque heure de retard à l\'antibiothérapie dans le sepsis = +7,6 % mortalité.' },
    ],
    objectives: [
      'Poser un questionnement systémique (tête aux pieds, OPQRST).',
      'Identifier les drapeaux rouges cachés derrière "je ne me sens pas bien".',
      'Reconnaître le sepsis débutant (fièvre + tachycardie + AEG).',
      'Différencier urgence vs consultation médicale routine.',
      'Maintenir une priorité par défaut élevée si flou persiste.',
    ],
    phases: [
      { n: 2, title: 'Symptôme principal',
        why: 'Force le patient/témoin à préciser.',
        how: ['« Quel est le symptôme le PLUS dérangeant ? »', '« Quand cela a-t-il commencé exactement ? »', '« Cela s\'aggrave-t-il, s\'améliore-t-il, reste stable ? »'],
        pitfalls: ['Accepter "je ne sais pas" sans relancer'] },
      { n: 3, title: 'Questionnaire systémique',
        why: 'Cherche les signes d\'urgence cachés.',
        how: ['« Fièvre ? Frissons ? »', '« Douleur thoracique, palpitations ? »', '« Difficulté à respirer ? »', '« Douleur abdominale, vomissements, diarrhée ? »', '« Faiblesse, étourdissements, fainting ? »', '« Saignements, sang dans les urines/selles ? »'],
        pitfalls: ['Bâcler l\'interrogatoire systémique'] },
      { n: 4, title: 'Antécédents et médication',
        why: 'Diabète, cancer, immunosuppression changent la priorité.',
        how: ['« A-t-il une maladie chronique connue ? »', '« Prend-il des médicaments réguliers ? »', '« A-t-il eu une intervention récente ? »', '« A-t-il pris ses médicaments aujourd\'hui ? »'],
        pitfalls: ['Oublier l\'immunosuppression'] },
    ],
    citizen_behavior: {
      title: 'Faire préciser un patient évasif',
      tone: ['Patient mais structuré', 'Questions ouvertes au début, fermées ensuite', 'Reformuler chaque réponse pour valider'],
      phrases_utiles: ['« Pouvez-vous me décrire ce que vous ressentez en une phrase ? »', '« Sur une échelle de 1 à 10, à quel point cela vous dérange ? »', '« Quand est la dernière fois que vous vous êtes senti normal ? »'],
      phrases_a_eviter: ['« Ce n\'est probablement rien »', '« Allez voir un médecin demain »', '« Buvez de l\'eau et reposez-vous »'],
      panique_gestion: ['Si patient hésite à appeler 911 → "vaut mieux pour rien que trop tard"', 'Si plusieurs symptômes → focaliser sur le pire d\'abord', 'Si patient âgé seul → vérifier réseau de soutien'],
    },
    red_flags: ['Confusion soudaine', 'Tachycardie + fièvre', 'Pâleur extrême', 'Sueurs profuses sans fièvre', 'Douleur thoracique même atypique', 'Symptômes neurologiques même légers', 'Vomissements persistants', 'Saignements', 'Antécédent immunosuppression', 'AEG (altération état général)'],
    pre_arrival: [
      'Position de confort (allongé ou semi-assis)',
      'Maintenir au chaud (couverture)',
      'NE PAS donner à boire ou manger',
      'Surveiller respiration et conscience',
      'Préparer liste de médicaments pour ambulanciers',
      'Identifier symptôme aggravant pour évaluation rapide',
      'Si patient seul → ouvrir porte d\'entrée pour ambulanciers',
    ],
    common_errors: [
      'Trier à la baisse à cause du flou',
      'Manquer un sepsis (fièvre + tachycardie + AEG)',
      'Sous-estimer chez patient âgé (présentations atypiques)',
      'Ne pas poser le questionnaire systémique complet',
    ],
    discussion_questions: [
      'Femme âgée "qui ne se sent pas bien", confuse, légèrement fébrile. Priorité ?',
      'Patient diabétique "fatigué" avec respiration légèrement rapide. Que craindre ?',
      'Comment poser questions structurées sans devenir robotique ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 26 — Sick Person',
      'Surviving Sepsis Campaign — Guidelines 2021',
      'CAEP — Canadian Triage Acuity Scale',
    ],
  },

  fievre: {
    title: 'Fièvre élevée',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `La fièvre n\'est pas une maladie mais un symptôme. Chez l\'adulte, > 39,5°C demande attention. Chez le nourrisson < 3 mois, TOUTE fièvre est urgente. Le danger principal : convulsions fébriles (enfants), méningite, sepsis. Le rôle du répartiteur : différencier fièvre simple vs fièvre signe d\'urgence.`,
    stats: [
      { icon: Heart, text: 'Toute fièvre chez un nourrisson < 3 mois est une urgence (méningite jusqu\'à preuve du contraire).' },
      { icon: AlertTriangle, text: 'Les convulsions fébriles touchent 3 % des enfants de 6 mois à 5 ans.' },
      { icon: Clock, text: 'Méningite à méningocoque : décès possible en 4-6 h.' },
    ],
    objectives: [
      'Identifier les âges critiques (< 3 mois, > 80 ans).',
      'Reconnaître les signes de méningite (raideur nucale, photophobie, pétéchies).',
      'Différencier fièvre simple vs sepsis.',
      'Guider mesures physiques (déshabillage, tiédir).',
      'Conseiller antipyrétiques appropriés.',
    ],
    phases: [
      { n: 2, title: 'Température et âge',
        why: 'Détermine immédiatement l\'urgence.',
        how: ['« Quelle est la température mesurée ? »', '« Mesurée comment (rectale, auriculaire, frontale) ? »', '« Quel âge a le patient ? »'],
        pitfalls: ['Accepter "il a chaud" sans thermomètre', 'Sous-estimer fièvre rectale (+0,5 °C par rapport à axillaire)'] },
      { n: 3, title: 'Symptômes associés',
        why: 'Cherche méningite, sepsis, infection grave.',
        how: ['« Mal de tête sévère ? Cou raide ? »', '« Vomissements ? Confusion ? »', '« Taches violettes sur la peau (pétéchies) ? »', '« Difficulté à respirer ? Pâleur ? »', '« Diarrhée ? Douleur en urinant ? »'],
        pitfalls: ['Ne pas tester la raideur nucale'] },
      { n: 4, title: 'Évolution',
        why: 'Fièvre persistante > 48 h ou en hausse = urgence.',
        how: ['« Depuis combien de jours ? »', '« A-t-il pris de l\'acétaminophène ou ibuprofène ? »', '« Ça a baissé puis remonté ? »', '« Mange-t-il, boit-il ? »'],
        pitfalls: ['Confondre fièvre baissée par antipyrétique et guérison'] },
    ],
    citizen_behavior: {
      title: 'Rassurer un parent paniqué',
      tone: ['Voix calme et pédagogique', 'Expliquer ce qu\'est la fièvre (réaction normale de défense)', 'Donner consignes concrètes'],
      phrases_utiles: ['« La fièvre n\'est pas dangereuse en soi, c\'est ce qu\'il y a derrière qu\'on cherche. »', '« Déshabillez-le, donnez à boire souvent. »', '« Une tiède éponge sur le front, pas glacée. »'],
      phrases_a_eviter: ['« Mettez-le dans un bain froid » (frissons aggravent la fièvre)', '« Frictionnez à l\'alcool » (toxique chez l\'enfant)', '« Ça va passer »'],
      panique_gestion: ['Si parent panique sur convulsion fébrile → expliquer que c\'est généralement bénin', 'Si plusieurs enfants à la maison → vérifier qu\'aucun autre n\'est symptomatique', 'Demander si vaccinations à jour'],
    },
    red_flags: ['< 3 mois avec toute fièvre', '> 40°C', 'Raideur nucale', 'Pétéchies (taches violettes ne disparaissant pas à la pression)', 'Convulsions', 'Confusion', 'Vomissements persistants', 'Pâleur + sueurs (sepsis)', 'Patient immunosupprimé', 'Voyage récent en zone tropicale'],
    pre_arrival: [
      'Déshabiller à l\'aise (sous-vêtements seulement)',
      'Linge tiède frais sur le front (jamais glacé)',
      'Donner à boire fréquemment (gorgées d\'eau)',
      'Acétaminophène ou ibuprofène SI pas déjà donné dans les 4-6 h',
      'NE PAS donner d\'aspirine à un enfant (syndrome de Reye)',
      'Allonger en position confortable',
      'Surveiller apparition de pétéchies (test du verre)',
    ],
    common_errors: [
      'Bain froid (provoque frissons et hausse de fièvre)',
      'Couvrir le patient pour qu\'il "transpire la fièvre"',
      'Donner aspirine à un enfant',
      'Sous-estimer fièvre chez nourrisson',
      'Ne pas chercher la raideur nucale',
    ],
    discussion_questions: [
      'Bébé de 2 mois avec fièvre à 38,2°C. Pourquoi urgence absolue ?',
      'Enfant de 4 ans avec fièvre 39°C et convulsion fébrile. Pronostic ?',
      'Adolescent avec fièvre + maux de tête + photophobie + pétéchies. Suspicion ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 26 — Sick Person',
      'Société canadienne de pédiatrie — Fièvre chez l\'enfant',
      'NICE — Feverish illness in children',
    ],
  },

  maux_tete: {
    title: 'Maux de tête sévères',
    subtitle: 'Guide pédagogique pour l\'enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `Le mal de tête est l\'un des motifs de consultation les plus fréquents. Le tri repose sur l\'identification des « red flags » qui distinguent une céphalée banale (tension, migraine) d\'une céphalée secondaire grave (HSA, méningite, AVC, tumeur). La règle d\'or : céphalée soudaine, intense, "comme un coup de tonnerre" = URGENCE.`,
    stats: [
      { icon: Heart, text: '15 % de la population souffre de migraines chroniques.' },
      { icon: AlertTriangle, text: 'L\'HSA (hémorragie sous-arachnoïdienne) tue 50 % des patients dans les 30 premiers jours.' },
      { icon: Clock, text: 'La fenêtre diagnostique pour une HSA est de 6 h (TDM négatif > 6 h moins fiable).' },
    ],
    objectives: [
      'Identifier la "céphalée en coup de tonnerre" (HSA).',
      'Reconnaître la méningite (fièvre + raideur nucale).',
      'Différencier migraine connue vs céphalée NOUVELLE.',
      'Évaluer les déficits neurologiques associés.',
      'Reconnaître l\'urgence absolue : « pire mal de tête de ma vie ».',
    ],
    phases: [
      { n: 2, title: 'Caractère du mal de tête',
        why: 'Identifie céphalée en coup de tonnerre (HSA).',
        how: ['« Est-ce que ça a commencé brusquement, en quelques secondes ? »', '« Est-ce le PIRE mal de tête de votre vie ? »', '« Sur 10, à quel point c\'est intense ? »', '« Où exactement — un côté, derrière, le front ? »'],
        pitfalls: ['Confondre installation progressive et brutale'] },
      { n: 3, title: 'Symptômes associés',
        why: 'Cherche signes d\'urgence neurologique.',
        how: ['« Avez-vous vomi sans nausée préalable ? »', '« Voyez-vous double, flou ? »', '« Engourdissement, faiblesse d\'un côté ? »', '« Fièvre, raideur du cou ? »', '« Photophobie, phonophobie ? »', '« Convulsions ou perte de conscience ? »'],
        pitfalls: ['Ne pas distinguer aura migraineuse et déficit AVC'] },
      { n: 4, title: 'Antécédents et déclencheurs',
        why: 'Migraine connue vs céphalée nouvelle.',
        how: ['« Avez-vous l\'habitude des migraines ? »', '« Est-ce différent d\'habitude ? »', '« Avez-vous eu un traumatisme crânien récent ? »', '« Prenez-vous des médicaments / anticoagulants ? »'],
        pitfalls: ['Penser que migraineux connu n\'a pas de nouvelle pathologie'] },
    ],
    citizen_behavior: {
      title: 'Évaluer la gravité d\'une céphalée',
      tone: ['Voix calme — le mal de tête est aggravé par stress', 'Pénombre et silence aident', 'Patient peut être confus, parler doucement'],
      phrases_utiles: ['« Allongez-vous dans le noir, au calme. »', '« Ne prenez rien tant qu\'on ne vous a pas vu. »', '« Décrivez-moi exactement comment ça a commencé. »'],
      phrases_a_eviter: ['« Prenez des Tylenol et attendez »', '« C\'est probablement le stress »', '« Buvez de l\'alcool, ça détend »'],
      panique_gestion: ['Si patient panique → environnement calme et sombre', 'Si proche présent → demander d\'observer évolution des symptômes', 'Préparer la liste de médicaments'],
    },
    red_flags: ['Céphalée en coup de tonnerre (instantanée)', '« Pire mal de tête de ma vie »', 'Vomissements en jet sans nausée', 'Photophobie + raideur nucale (méningite)', 'Déficit neurologique focal', 'Convulsions', 'Trouble de conscience', 'Fièvre + céphalée', 'Patient âgé avec nouvelle céphalée', 'Traumatisme crânien récent', 'Anticoagulants', 'Immunosuppression'],
    pre_arrival: [
      'Allongé dans une pièce sombre et calme',
      'NE PAS donner d\'analgésique en attendant (peut masquer)',
      'NE PAS donner à manger / boire si vomissements',
      'Si suspicion méningite → isolement du proche aussi (masque)',
      'Surveiller conscience',
      'Préparer la liste des médicaments',
      'Noter l\'heure exacte de début',
    ],
    common_errors: [
      'Trier vers le bas à cause de migraine connue',
      'Sous-estimer une céphalée en coup de tonnerre',
      'Confondre aura migraineuse et AVC débutant',
      'Donner de l\'aspirine "au cas où"',
    ],
    discussion_questions: [
      'Patient de 45 ans, mal de tête soudain à 10/10 « comme un coup de marteau ». Suspicion ?',
      'Migraine connue mais aujourd\'hui « différente » avec faiblesse du bras. Action ?',
      'Adolescent avec mal de tête + fièvre + raideur nucale. Mesures à prendre ?',
    ],
    references: [
      'Clawson J., MPDS Protocol 18 — Headache',
      'Ottawa SAH Rule — Perry JJ 2013',
      'Société canadienne des céphalées — Lignes directrices',
    ],
  },

});
